import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, startOfDay, isSameDay, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Wallet, Loader2, CheckCircle2, Clock, Search, X, RefreshCw, ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { SingleDayFilter, QuickRangeButtons } from '../components/dashboard/FilterComponents';
import { AssignPayPalDialog } from '../components/paypal/AssignPayPalDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-US', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const Stat = ({ label, value, color, testid }) => (
    <Card className="border-0 shadow-sm"><CardContent className="p-4">
        <p className="text-xs text-slate-500">{label}</p>
        <p className={`text-xl font-bold ${color}`} data-testid={testid}>{value}</p>
    </CardContent></Card>
);

const StatusBadge = ({ tx }) => {
    if (tx.confirmed) return <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full truncate" title={tx.invoice?.user_name}><CheckCircle2 className="w-3 h-3 shrink-0" /> Confirmada · {tx.invoice?.user_name}</span>;
    if (tx.on_hold) return <span className="inline-flex items-center gap-1 text-xs text-orange-800 bg-orange-100 px-2 py-0.5 rounded-full" title="PayPal retiene este pago temporalmente (24 h). Solo el administrador puede confirmarlo manualmente." data-testid={`paypal-hold-${tx.email_id}`}><ShieldAlert className="w-3 h-3" /> Retenido</span>;
    return <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" /> Pendiente</span>;
};

const canConfirm = (tx, isAdmin) => !tx.confirmed && (isAdmin || !tx.on_hold);

const TxRow = ({ tx, onConfirm, busy, isAdmin }) => (
    <tr className={`border-b border-slate-100 ${tx.confirmed ? 'bg-green-50/40' : tx.on_hold ? 'bg-orange-50/40' : ''}`} data-testid={`paypal-tx-${tx.email_id}`}>
        <td className="py-3 px-3 text-slate-500 whitespace-nowrap text-xs">{fmtDate(tx.date)}</td>
        <td className="py-3 px-3 font-medium text-slate-800">{tx.sender_name}</td>
        <td className="py-3 px-3 text-right whitespace-nowrap">{money(tx.gross_amount)}</td>
        <td className="py-3 px-3 text-right whitespace-nowrap">
            {tx.has_fee ? <span className="text-red-600">-{money(tx.fee_amount)}</span> : <span className="text-slate-400 text-xs">sin comisión</span>}
        </td>
        <td className="py-3 px-3 text-right font-bold text-sky-700 whitespace-nowrap" data-testid={`paypal-net-${tx.email_id}`}>
            {money(tx.net_amount)}
            {tx.edited && <span className="ml-1 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title="Monto editado por el administrador" data-testid={`paypal-edited-${tx.email_id}`}>editado</span>}
        </td>
        <td className="py-3 px-3"><StatusBadge tx={tx} /></td>
        <td className="py-3 px-3 text-right">
            {canConfirm(tx, isAdmin) && (
                <Button size="sm" className={`${tx.on_hold ? 'bg-orange-600 hover:bg-orange-700' : 'bg-sky-600 hover:bg-sky-700'} text-white`} disabled={busy === tx.email_id} onClick={() => onConfirm(tx)} data-testid={`paypal-confirm-${tx.email_id}`}>
                    {busy === tx.email_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> {tx.on_hold ? 'Confirmar manual' : 'Confirmar'}</>}
                </Button>
            )}
        </td>
    </tr>
);

const TxCard = ({ tx, onConfirm, busy, isAdmin }) => (
    <div className={`rounded-xl border p-3 ${tx.confirmed ? 'bg-green-50/50 border-green-200' : tx.on_hold ? 'bg-orange-50/50 border-orange-200' : 'bg-white border-slate-200'}`} data-testid={`paypal-card-${tx.email_id}`}>
        <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
                <p className="font-semibold text-slate-800 leading-tight">{tx.sender_name}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{fmtDate(tx.date)}</p>
            </div>
            <div className="text-right">
                <p className="text-lg font-bold text-sky-700 whitespace-nowrap" data-testid={`paypal-net-${tx.email_id}`}>{money(tx.net_amount)}</p>
                {tx.edited && <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`paypal-edited-${tx.email_id}`}>editado</span>}
            </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>Recibido {money(tx.gross_amount)}{tx.has_fee ? <> · Comisión <span className="text-red-600">-{money(tx.fee_amount)}</span></> : ' · sin comisión'}</span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
            <StatusBadge tx={tx} />
            {canConfirm(tx, isAdmin) && (
                <Button size="sm" className={`${tx.on_hold ? 'bg-orange-600 hover:bg-orange-700' : 'bg-sky-600 hover:bg-sky-700'} text-white h-9 px-4`} disabled={busy === tx.email_id} onClick={() => onConfirm(tx)} data-testid={`paypal-confirm-${tx.email_id}`}>
                    {busy === tx.email_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> {tx.on_hold ? 'Confirmar manual' : 'Confirmar'}</>}
                </Button>
            )}
        </div>
    </div>
);

const SORT_KEYS = { date: (t) => t.date || '', name: (t) => (t.sender_name || '').toLowerCase(), gross: (t) => t.gross_amount, fee: (t) => t.fee_amount, net: (t) => t.net_amount };

const sortItems = (items, sort) => {
    const get = SORT_KEYS[sort.key] || SORT_KEYS.date;
    return [...items].sort((a, b) => {
        const va = get(a), vb = get(b);
        const r = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), 'es');
        return sort.dir === 'asc' ? r : -r;
    });
};

const SortIcon = ({ active, dir }) => (
    active ? (dir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-sky-600" /> : <ArrowDown className="w-3.5 h-3.5 text-sky-600" />) : <ArrowUpDown className="w-3.5 h-3.5 text-slate-300" />
);

const Th = ({ label, k, sort, onSort, right }) => (
    <th className={`py-3 px-3 ${right ? 'text-right' : 'text-left'}`}>
        <button type="button" onClick={() => onSort(k)} className={`inline-flex items-center gap-1 hover:text-sky-700 ${right ? 'flex-row-reverse' : ''}`} data-testid={`paypal-sort-${k}`}>
            {label} <SortIcon active={sort.key === k} dir={sort.dir} />
        </button>
    </th>
);

const SORT_OPTIONS = [
    { key: 'date', label: 'Fecha' }, { key: 'name', label: 'Nombre' }, { key: 'gross', label: 'Recibido' }, { key: 'fee', label: 'Comisión' }, { key: 'net', label: 'Neto' },
];

const MobileSortBar = ({ sort, onSort }) => (
    <div className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid="paypal-mobile-sort">
        <span className="text-xs text-slate-500 shrink-0 mr-1">Ordenar:</span>
        {SORT_OPTIONS.map((o) => (
            <button key={o.key} type="button" onClick={() => onSort(o.key)}
                className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 h-8 text-xs ${sort.key === o.key ? 'bg-sky-500 border-sky-500 text-white' : 'bg-white border-slate-200 text-slate-600'}`}
                data-testid={`paypal-msort-${o.key}`}>
                {o.label}{sort.key === o.key && (sort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
            </button>
        ))}
    </div>
);

const PAGE_SIZE = 10;

const Pager = ({ page, pages, total, onPage }) => (
    <div className="flex items-center justify-between gap-2 px-1 py-2 text-sm" data-testid="paypal-pager">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="paypal-prev-page"><ChevronLeft className="w-4 h-4 mr-1" /> Anterior</Button>
        <span className="text-slate-500 text-xs md:text-sm text-center" data-testid="paypal-page-info">
            Página <b className="text-slate-800">{page}</b> de {pages} · {total} operaciones
        </span>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="paypal-next-page">Siguiente <ChevronRight className="w-4 h-4 ml-1" /></Button>
    </div>
);

export default function PayPalTransactions() {
    const { user, getAuthHeaders } = useAuth();
    const isAdmin = user?.role === 'admin';
    const today = startOfDay(new Date());
    const minDate = isAdmin ? null : subDays(today, 30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');
    // PayPal users only see the day's accounting (today by default); admin sees everything
    const [dateFrom, setDateFrom] = useState(isAdmin ? null : today);
    const [dateTo, setDateTo] = useState(isAdmin ? null : today);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (status !== 'all') params.status = status;
            if (dateFrom) params.date_from = format(dateFrom, 'yyyy-MM-dd');
            if (dateTo) params.date_to = format(dateTo, 'yyyy-MM-dd');
            const r = await axios.get(`${API}/api/paypal/transactions`, { params, headers: getAuthHeaders() });
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al cargar transacciones PayPal');
        } finally {
            setLoading(false);
        }
    }, [q, status, dateFrom, dateTo, getAuthHeaders]);

    useEffect(() => { load(); }, [load]);

    const [assignTx, setAssignTx] = useState(null);
    const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
    const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }));
    const sorted = data ? sortItems(data.items, sort) : [];
    const [page, setPage] = useState(1);
    useEffect(() => { setPage(1); }, [q, status, dateFrom, dateTo, sort]);
    const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const safePage = Math.min(page, pages);
    const pageItems = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const pager = sorted.length > PAGE_SIZE && <Pager page={safePage} pages={pages} total={sorted.length} onPage={setPage} />;

    const doConfirm = async (tx, userId) => {
        setBusy(tx.email_id);
        try {
            const r = await axios.post(`${API}/api/paypal/transactions/confirm`, { email_id: tx.email_id, user_id: userId || undefined }, { headers: getAuthHeaders() });
            toast.success(r.data.message);
            setAssignTx(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo confirmar');
        } finally {
            setBusy(null);
        }
    };

    const confirm = async (tx) => {
        if (isAdmin) { setAssignTx(tx); return; }
        if (!window.confirm(`¿Confirmar ${tx.sender_name} por ${money(tx.net_amount)} (neto)? Se contabilizará en tus confirmados.`)) return;
        await doConfirm(tx, null);
    };

    const clear = () => { setQ(''); setStatus('all'); setDateFrom(isAdmin ? null : today); setDateTo(isAdmin ? null : today); };
    const hasFilters = q || status !== 'all' || (isAdmin ? (dateFrom || dateTo) : !(dateFrom && dateTo && isSameDay(dateFrom, today) && isSameDay(dateTo, today)));
    const dayLabel = dateFrom && dateTo && isSameDay(dateFrom, dateTo) ? format(dateFrom, "EEEE d 'de' MMMM", { locale: es }) : null;
    const dayNet = data ? data.items.reduce((s, i) => s + (i.confirmed ? i.net_amount : 0), 0) : 0;

    return (
        <div className="space-y-4 md:space-y-6" data-testid="paypal-transactions-page">
            {isAdmin && <AssignPayPalDialog tx={assignTx} open={!!assignTx} onOpenChange={(o) => { if (!o) setAssignTx(null); }} onConfirm={doConfirm} busy={!!busy} />}
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-800 font-['Manrope'] flex items-center gap-2 md:gap-3">
                        <span className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-[#003087] flex items-center justify-center shrink-0"><Wallet className="w-5 h-5 text-white" /></span>
                        <span className="truncate">PayPal</span>
                    </h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        {isAdmin ? <>Tal como llegan en los correos: bruto, comisión y total neto. Al confirmar se contabiliza el <b>neto</b>.</> : <>Transacciones del día · al confirmar se contabiliza el <b>neto</b>.</>}
                    </p>
                </div>
                <Button variant="outline" size="icon" onClick={load} disabled={loading} className="shrink-0" title="Actualizar" data-testid="paypal-refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
            </div>

            {data && isAdmin && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Stat label="Pendientes" value={`${data.pending_count} · ${money(data.pending_net_total)}`} color="text-amber-600" testid="paypal-stat-pending" />
                    <Stat label="Confirmadas" value={`${data.confirmed_count} · ${money(data.confirmed_net_total)}`} color="text-green-600" testid="paypal-stat-confirmed" />
                    <Stat label="Comisiones PayPal" value={money(data.fees_total)} color="text-red-600" testid="paypal-stat-fees" />
                    <Stat label={data.hold_count ? 'Transacciones · Retenidas' : 'Transacciones'} value={data.hold_count ? `${data.count} · ${data.hold_count} (${money(data.hold_net_total)})` : data.count} color="text-slate-800" testid="paypal-stat-count" />
                </div>
            )}

            <Card className="border-0 shadow-sm"><CardContent className="p-3 md:p-4 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <SingleDayFilter day={dateFrom && dateTo && isSameDay(dateFrom, dateTo) ? dateFrom : null} onSelectDay={(d) => { setDateFrom(d); setDateTo(d); }} testId="paypal-single-day-btn" minDate={minDate} />
                    <QuickRangeButtons onSelectRange={(f, t) => { setDateFrom(f); setDateTo(t); }} activeFrom={dateFrom} activeTo={dateTo} testIdPrefix="paypal" />
                    {hasFilters && <Button variant="ghost" size="sm" onClick={clear} data-testid="paypal-clear-btn"><X className="w-4 h-4 mr-1" /> {isAdmin ? 'Limpiar' : 'Hoy'}</Button>}
                </div>
                {!isAdmin && <p className="text-[11px] text-slate-400" data-testid="paypal-window-note">Solo puedes consultar los últimos 30 días (desde el {format(minDate, 'd MMM', { locale: es })}).</p>}
                <div className="flex items-center gap-2">
                    <div className="relative flex-1 min-w-0">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre o monto" className="pl-9" data-testid="paypal-search-input" />
                    </div>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm bg-white shrink-0" data-testid="paypal-status-filter">
                        <option value="all">Todas</option><option value="pending">Pendientes</option><option value="confirmed">Confirmadas</option>
                    </select>
                </div>
            </CardContent></Card>

            {data && dayLabel && (
                <div className="flex items-center justify-between px-1 text-sm" data-testid="paypal-day-summary">
                    <span className="font-medium text-slate-700 capitalize">{dayLabel}</span>
                    <span className="text-slate-500">{data.count} transacciones · <span className="text-green-700 font-semibold">{money(dayNet)}</span> confirmado</span>
                </div>
            )}

            {loading && !data ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
            ) : !data?.items?.length ? (
                <Card className="border-0 shadow-sm"><CardContent className="py-16 text-center text-slate-500" data-testid="paypal-empty"><Wallet className="w-12 h-12 mx-auto mb-3 text-slate-300" />No hay transacciones PayPal {dayLabel ? `el ${dayLabel}` : hasFilters ? 'con esos filtros' : 'en los últimos días'}</CardContent></Card>
            ) : (
                <>
                    {/* Mobile: cards */}
                    <div className="md:hidden space-y-2" data-testid="paypal-cards">
                        <MobileSortBar sort={sort} onSort={onSort} />
                        {pageItems.map((tx) => <TxCard key={tx.email_id} tx={tx} onConfirm={confirm} busy={busy} isAdmin={isAdmin} />)}
                        {pager}
                    </div>
                    {/* Desktop: table */}
                    <Card className="border-0 shadow-sm hidden md:block"><CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="paypal-table">
                                <thead><tr className="text-slate-500 border-b bg-slate-50">
                                    <Th label="Fecha" k="date" sort={sort} onSort={onSort} />
                                    <Th label="Remitente" k="name" sort={sort} onSort={onSort} />
                                    <Th label="Dinero recibido" k="gross" sort={sort} onSort={onSort} right />
                                    <Th label="Comisión" k="fee" sort={sort} onSort={onSort} right />
                                    <Th label="Total neto" k="net" sort={sort} onSort={onSort} right />
                                    <th className="py-3 px-3 text-left">Estado</th><th className="py-3 px-3"></th>
                                </tr></thead>
                                <tbody>{pageItems.map((tx) => <TxRow key={tx.email_id} tx={tx} onConfirm={confirm} busy={busy} isAdmin={isAdmin} />)}</tbody>
                            </table>
                        </div>
                        {pager}
                    </CardContent></Card>
                </>
            )}
        </div>
    );
}
